import {
  Component,
  ElementRef,
  EventEmitter,
  HostListener,
  Input,
  OnInit,
  Output,
} from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import { ProfileService } from 'src/app/services/profile.service';
import { Subscription } from 'rxjs';
import { navigation } from 'src/app/constants/navigation.const';
import { MenuAccessService } from 'src/app/services/menu-access.service';
import { environment } from 'src/environments/environment';
import { Profile } from 'src/app/models/profile.model';
import { NavigationType } from 'src/app/types/navigation.type';

interface IMenuAccess {
  name: string;
  access: boolean;
}

@Component({
  standalone: false,
  selector: 'app-sidebar',
  templateUrl: './sidebar.component.html',
  styleUrls: ['./sidebar.component.scss'],
})
export class SidebarComponent implements OnInit {
  @Output() isActive = new EventEmitter();
  @Input() active: boolean = false;
  isHovered: boolean = false;

  activeButton: string = 'dashboard';
  profileData!: Profile;
  private profileSubscription!: Subscription;
  isShowDoc: boolean = true;
  isShowBillings: boolean = true;
  isShowSimulator: boolean = true;
  isProfileReady: boolean = false;

  menuItems: NavigationType[] = navigation;
  activeUrl: string = this.route.url;

  /**
   * Here we initialize the sidebar.
   * We also listen for route changes to keep the active menu highlighted.
   */
  constructor(
    private elementRef: ElementRef,
    private route: Router,
    private profileService: ProfileService,
    private menuAccessService: MenuAccessService,
  ) {
    this.route.events.subscribe((val) => {
      if (val instanceof NavigationEnd) {
        const url = val.url || this.route.url;
        this.activeUrl = url;
        this.setActiveButtonFromUrl();
      }
    });
  }

  /**
   * This function checks if you clicked outside the sidebar.
   * If so, it tells the layout to close it—essential for mobile view!
   */
  @HostListener('document:click', ['$event', '$event.target'])
  public onClick(event: MouseEvent, targetElement: EventTarget | null): void {
    if (!targetElement || !(targetElement instanceof HTMLElement)) return;

    const clickedInside = this.elementRef.nativeElement.contains(targetElement);
    if (!clickedInside) {
      this.isActive.emit(false);
    }
  }

  /**
   * When the sidebar initializes, fetch the user's profile data.
   * Check their RajaOngkir level to decide which menus should be displayed.
   */
  ngOnInit() {
    const mode = localStorage.getItem('mode');
    this.setActiveButtonFromUrl();

    this.profileSubscription = this.profileService.profileData$.subscribe(
      (data) => {
        if (data) {
          this.profileData = data;

          const { rajaongkir_level, is_openapi, is_lifetime } = data;
          const isAdvancedUser = is_openapi || is_lifetime;

          this.isShowSimulator = !!(
            rajaongkir_level === 3 &&
            mode !== 'production'
          );
          this.isProfileReady = true;

          const restrictedMenusForStarterPro = ['Shipping API', 'Teams', 'Expedition'];

          /**
           * The function that filters menu list!
           * It recursively checks every item, including sub-menus, to ensure no
           * restricted items slip through.
           */
          const filterNavigation = (menus: NavigationType[], accessNames: string[] | null): NavigationType[] => {
            return menus
              .map((menu) => {
                if (rajaongkir_level !== 3 && restrictedMenusForStarterPro.includes(menu.name)) {
                  return null;
                }
                if (menu.children) {
                  const filteredChildren = filterNavigation(menu.children, accessNames);
                  if (filteredChildren.length > 0) {
                    return { ...menu, children: filteredChildren };
                  }
                  return null;
                }
                if (accessNames) {
                  return accessNames.includes(menu.name) ? menu : null;
                }
                return menu;
              })
              .filter((item): item is NavigationType => item !== null);
          };

          if (data.login_as_team === true) {
            this.menuAccessService.fetchMenuAccess().subscribe(
              (res: IMenuAccess[]) => {
                const accessNames = res
                  .filter((item) => item.access === true)
                  .map((item) => item.name);

                if (!accessNames.includes('Profile')) {
                  accessNames.push('Profile');
                }

                this.menuItems = filterNavigation(navigation, accessNames);
                this.setActiveButtonFromUrl();
              },
              (error) => {
                console.error('Error fetching menu access', error);
                this.menuItems = filterNavigation(navigation, null);
                this.setActiveButtonFromUrl();
              }
            );
          } else {
            this.menuItems = filterNavigation(navigation, null);
            this.setActiveButtonFromUrl();
          }
        }
      }
    );
  }

  /**
   * This function finds the menu that matches the current URL.
   * The matching menu name is stored in 'activeButton' to highlight it in the UI.
   */
  setActiveButtonFromUrl() {
    const path = this.activeUrl ? this.activeUrl : this.route.url;

    let matchedItem: NavigationType | null = null;
    let matchedParent: NavigationType | null = null;

    // Reset open state for all items first
    const resetOpen = (items: NavigationType[]) => {
      for (const item of items) {
        item.open = false;
        if (item.children) {
          resetOpen(item.children);
        }
      }
    };
    resetOpen(this.menuItems);

    /**
     * A helper to look for URL matches deep within sub-menus.
     */
    const findMatch = (items: NavigationType[], parent: NavigationType | null = null) => {
      for (const item of items) {
        if (item.link && (path === item.link || (item.link !== '/' && path.startsWith(item.link)))) {
          if (!matchedItem || item.link.length > (matchedItem.link?.length || 0)) {
            matchedItem = item;
            matchedParent = parent;
          }
        }
        if (item.children) {
          findMatch(item.children, item);
        }
      }
    };

    findMatch(this.menuItems);

    if (matchedItem) {
      this.activeButton = (matchedItem as NavigationType).name;
      if (matchedParent) {
        (matchedParent as NavigationType).open = true;
      }
    }
  }

  /**
   * Clean up
   * When the sidebar is destroyed, we unsubscribe from profile data to prevent memory leaks.
   */
  ngOnDestroy(): void {
    if (this.profileSubscription) {
      this.profileSubscription.unsubscribe();
    }
  }
}
